import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ArrowDownTrayIcon,
  BookOpenIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartOutline } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import {
  deleteBookFile,
  getBookFile,
  getDownloadedIds,
  getItem,
  removeDownloadedId,
} from '../utils/storage';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const FAVORITES_KEY = 'favorites';
const PDF_WORKER_URL =
  'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
const BASE64_CHUNK_SIZE = 512 * 1024; // Divisible by four.

const readFavorites = () => {
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const yieldToBrowser = () =>
  new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });

const createAbortError = () => {
  const error = new Error('Opening cancelled');
  error.name = 'AbortError';
  return error;
};

const base64ToPdfBlob = async (
  storedValue,
  onProgress,
  isCancelled
) => {
  const value = String(storedValue || '');
  const commaIndex = value.indexOf(',');
  const base64 = (commaIndex >= 0
    ? value.slice(commaIndex + 1)
    : value
  ).replace(/\s/g, '');

  if (!base64) throw new Error('The downloaded PDF is empty.');

  const byteChunks = [];

  for (
    let offset = 0;
    offset < base64.length;
    offset += BASE64_CHUNK_SIZE
  ) {
    if (isCancelled()) throw createAbortError();

    const encodedChunk = base64.slice(
      offset,
      offset + BASE64_CHUNK_SIZE
    );
    const binaryChunk = atob(encodedChunk);
    const bytes = new Uint8Array(binaryChunk.length);

    for (let index = 0; index < binaryChunk.length; index += 1) {
      bytes[index] = binaryChunk.charCodeAt(index);
    }

    byteChunks.push(bytes);

    const processed = Math.min(
      offset + BASE64_CHUNK_SIZE,
      base64.length
    );
    onProgress(
      Math.min(92, Math.max(5, Math.round((processed / base64.length) * 92)))
    );

    if (processed < base64.length) await yieldToBrowser();
  }

  if (isCancelled()) throw createAbortError();

  const blob = new Blob(byteChunks, { type: 'application/pdf' });
  if (blob.size === 0) throw new Error('The downloaded PDF is empty.');
  return blob;
};

export default function Books() {
  const [downloadedBooks, setDownloadedBooks] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [removingIds, setRemovingIds] = useState(() => new Set());
  const [favorites, setFavorites] = useState(readFavorites);
  const [selectedBook, setSelectedBook] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [pdfError, setPdfError] = useState('');

  const blobUrlRef = useRef(null);
  const openRequestIdRef = useRef(0);
  const mountedRef = useRef(true);

  // This plugin uses React hooks internally. It must be called
  // unconditionally on every render and must not be wrapped in useMemo.
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar: (Toolbar) => (props) => (
      <Toolbar {...props}>
        {(slots) => {
          const visibleSlots = Object.entries(slots).filter(
            ([name]) => name !== 'download' && name !== 'print'
          );

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {visibleSlots.map(([name, slot]) => (
                <React.Fragment key={name}>{slot}</React.Fragment>
              ))}
            </div>
          );
        }}
      </Toolbar>
    ),
  });

  const favoriteIdSet = useMemo(
    () => new Set(favorites.map((id) => String(id))),
    [favorites]
  );

  const loadDownloadedBooks = useCallback(async () => {
    setLoadingBooks(true);
    setLoadError('');

    try {
      const [ids, cachedValue] = await Promise.all([
        getDownloadedIds(),
        getItem('books_cache'),
      ]);
      const cachedBooks = Array.isArray(cachedValue) ? cachedValue : [];
      const books = ids.map((id) => {
        const cachedBook = cachedBooks.find(
          (book) => String(book.id) === String(id)
        );

        return (
          cachedBook || {
            id,
            title: `Book ${id}`,
            author: 'Unknown author',
            cover: null,
          }
        );
      });

      if (mountedRef.current) setDownloadedBooks(books);
    } catch (error) {
      console.error('Failed to load downloaded books:', error);
      if (mountedRef.current) {
        setLoadError('Unable to load your downloaded books.');
      }
    } finally {
      if (mountedRef.current) setLoadingBooks(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadDownloadedBooks();

    return () => {
      mountedRef.current = false;
      openRequestIdRef.current += 1;

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [loadDownloadedBooks]);

  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
    } catch (_) {
      // Favorites still work for this session when storage is unavailable.
    }
  }, [favorites]);

  useEffect(() => {
    if (!selectedBook) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        openRequestIdRef.current += 1;
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
        setSelectedBook(null);
        setPdfLoading(false);
        setPdfError('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedBook]);

  const toggleFavorite = useCallback((bookId, event) => {
    event?.stopPropagation();

    setFavorites((previous) => {
      const bookKey = String(bookId);
      const exists = previous.some((id) => String(id) === bookKey);
      return exists
        ? previous.filter((id) => String(id) !== bookKey)
        : [...previous, bookId];
    });
  }, []);

  const removeBook = useCallback(async (bookId, event) => {
    event?.stopPropagation();
    const bookKey = String(bookId);

    setRemovingIds((previous) => {
      if (previous.has(bookKey)) return previous;
      const next = new Set(previous);
      next.add(bookKey);
      return next;
    });

    try {
      await Promise.all([
        deleteBookFile(bookId),
        removeDownloadedId(bookId),
      ]);

      if (mountedRef.current) {
        setDownloadedBooks((previous) =>
          previous.filter(
            (book) => String(book.id) !== bookKey
          )
        );
      }
    } catch (error) {
      console.error('Failed to remove downloaded book:', error);
      if (mountedRef.current) {
        setLoadError('Could not remove that book. Please try again.');
      }
    } finally {
      if (mountedRef.current) {
        setRemovingIds((previous) => {
          const next = new Set(previous);
          next.delete(bookKey);
          return next;
        });
      }
    }
  }, []);

  const closeReader = useCallback(() => {
    openRequestIdRef.current += 1;

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    setSelectedBook(null);
    setPdfLoading(false);
    setPdfProgress(0);
    setPdfError('');
  }, []);

  const openBook = useCallback(async (book) => {
    const requestId = openRequestIdRef.current + 1;
    openRequestIdRef.current = requestId;

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    setSelectedBook({ ...book, pdfUrl: null });
    setPdfLoading(true);
    setPdfProgress(2);
    setPdfError('');

    try {
      const storedFile = await getBookFile(book.id);

      if (requestId !== openRequestIdRef.current) {
        throw createAbortError();
      }

      if (!storedFile) {
        throw new Error('The downloaded PDF was not found on this device.');
      }

      const blob = await base64ToPdfBlob(
        storedFile,
        (progress) => {
          if (
            mountedRef.current &&
            requestId === openRequestIdRef.current
          ) {
            setPdfProgress(progress);
          }
        },
        () => requestId !== openRequestIdRef.current
      );

      if (requestId !== openRequestIdRef.current) {
        throw createAbortError();
      }

      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setPdfProgress(96);
      setSelectedBook({ ...book, pdfUrl: url });
    } catch (error) {
      if (
        error.name !== 'AbortError' &&
        mountedRef.current &&
        requestId === openRequestIdRef.current
      ) {
        console.error('Open book error:', error);
        setPdfLoading(false);
        setPdfError(error.message || 'Failed to open the PDF.');
      }
    }
  }, []);

  const handleDocumentLoad = useCallback(() => {
    setPdfProgress(100);
    requestAnimationFrame(() => setPdfLoading(false));
  }, []);

  const handleDocumentError = useCallback((error) => {
    console.error('PDF render error:', error);
    setPdfLoading(false);
    setPdfError('The PDF could not be rendered.');
  }, []);

  return (
    <>
      <style>{`
        .downloaded-books-page {
          -webkit-tap-highlight-color: transparent;
        }
        .downloaded-book-card {
          content-visibility: auto;
          contain: layout paint style;
          contain-intrinsic-size: 275px;
          backface-visibility: hidden;
          animation: downloaded-book-enter 0.42s cubic-bezier(0.22, 1, 0.36, 1) backwards;
        }
        @keyframes downloaded-book-enter {
          from { opacity: 0; transform: translateY(14px) scale(0.99); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes downloaded-reader-enter {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .downloaded-reader-modal {
          animation: downloaded-reader-enter 0.18s ease-out;
        }
        .rpv-default-layout__sidebar,
        .rpv-default-layout__toolbar-sidebar-toggle,
        [aria-label="Toggle sidebar"],
        [title="Toggle sidebar"],
        .rpv-core__download-button,
        [aria-label="Download"],
        [title="Download"],
        .rpv-core__print-button,
        [aria-label="Print"],
        [title="Print"],
        .rpv-core__menu-item[aria-label="Download"],
        .rpv-core__menu-item[aria-label="Print"] {
          display: none !important;
        }
        .rpv-default-layout__main {
          width: 100% !important;
          margin-left: 0 !important;
        }
        .rpv-default-layout__body {
          grid-template-columns: 0fr 1fr !important;
        }
        .rpv-default-layout__sidebar {
          width: 0 !important;
          min-width: 0 !important;
          overflow: hidden !important;
        }
        .rpv-core__viewer {
          width: 100% !important;
          height: 100% !important;
        }
        .rpv-core__inner-page,
        .rpv-core__canvas-layer {
          max-width: 100% !important;
        }
        .rpv-core__page-layer {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .rpv-core__canvas-layer,
        .rpv-core__canvas-layer canvas {
          height: auto !important;
        }
        .rpv-core__canvas-layer canvas {
          max-width: 100%;
          width: auto !important;
        }
        @media (prefers-reduced-motion: reduce) {
          .downloaded-book-card { animation: none !important; }
          .downloaded-reader-modal { animation: none !important; }
          * { scroll-behavior: auto !important; }
        }
      `}</style>

      <main className="downloaded-books-page max-w-6xl mx-auto px-3 sm:px-5 lg:px-6 py-5 sm:py-7">
        <header className="flex items-center justify-between gap-4 mb-5 sm:mb-7">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-none">
              <ArrowDownTrayIcon className="w-5 h-5 text-primary-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800 truncate">
                My Books
              </h1>
              <p className="text-xs sm:text-sm text-gray-500">
                {downloadedBooks.length}{' '}
                {downloadedBooks.length === 1 ? 'book' : 'books'} available offline
              </p>
            </div>
          </div>
        </header>

        {loadError && (
          <div className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{loadError}</span>
            <button
              type="button"
              onClick={loadDownloadedBooks}
              className="font-semibold hover:text-red-900"
            >
              Retry
            </button>
          </div>
        )}

        {loadingBooks ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-xl border border-gray-100 bg-white animate-pulse"
              >
                <div className="h-40 sm:h-44 bg-gray-100" />
                <div className="p-3 space-y-2">
                  <div className="h-4 rounded bg-gray-100" />
                  <div className="h-3 w-2/3 rounded bg-gray-100" />
                </div>
              </div>
            ))}
          </div>
        ) : downloadedBooks.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 sm:p-14 text-center shadow-sm">
            <div className="w-20 h-20 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-5">
              <ArrowDownTrayIcon className="w-10 h-10 text-gray-300" />
            </div>
            <h2 className="text-xl font-semibold text-gray-700">
              No downloaded books
            </h2>
            <p className="text-gray-400 mt-2 max-w-md mx-auto">
              Books downloaded from the library for offline reading will appear here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
            {downloadedBooks.map((book, index) => {
              const bookKey = String(book.id);
              const isFavorite = favoriteIdSet.has(bookKey);
              const isRemoving = removingIds.has(bookKey);

              return (
                <article
                  key={book.id}
                  onClick={() => !isRemoving && openBook(book)}
                  className={`downloaded-book-card group overflow-hidden rounded-xl border bg-white shadow-sm transform-gpu transition-[transform,box-shadow,border-color,opacity] duration-200 ease-out ${
                    isRemoving
                      ? 'opacity-60 cursor-wait border-gray-100'
                      : 'cursor-pointer border-gray-100 hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-md'
                  }`}
                  style={{ animationDelay: `${Math.min(index, 12) * 25}ms` }}
                >
                  <div className="relative h-36 sm:h-44 bg-gray-50 flex items-center justify-center overflow-hidden">
                    {book.cover ? (
                      <img
                        src={book.cover}
                        alt={book.title}
                        className="h-full w-full object-contain bg-white transition-transform duration-300 ease-out group-hover:scale-[1.015]"
                        loading="lazy"
                        decoding="async"
                        draggable="false"
                      />
                    ) : (
                      <BookOpenIcon className="w-12 sm:w-16 h-12 sm:h-16 text-gray-300 transition-colors group-hover:text-primary-300" />
                    )}

                    <button
                      type="button"
                      onClick={(event) =>
                        toggleFavorite(book.id, event)
                      }
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm border border-gray-200 shadow-sm flex items-center justify-center transition-[transform,background-color] duration-200 hover:bg-white active:scale-90"
                      aria-label={
                        isFavorite
                          ? `Remove ${book.title} from favorites`
                          : `Add ${book.title} to favorites`
                      }
                      aria-pressed={isFavorite}
                    >
                      {isFavorite ? (
                        <HeartSolid className="w-5 h-5 text-red-500" />
                      ) : (
                        <HeartOutline className="w-5 h-5 text-gray-500 hover:text-red-400" />
                      )}
                    </button>
                  </div>

                  <div className="p-3 sm:p-4">
                    <h2
                      className="font-semibold text-sm sm:text-base text-gray-800 truncate"
                      title={book.title}
                    >
                      {book.title}
                    </h2>
                    <div className="mt-1 flex items-center gap-1 text-xs sm:text-sm text-gray-500 min-w-0">
                      <UserIcon className="w-3.5 h-3.5 flex-none" />
                      <span className="truncate">
                        {book.author || 'Unknown author'}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={(event) =>
                        removeBook(book.id, event)
                      }
                      disabled={isRemoving}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-red-500 transition-colors hover:text-red-700 disabled:cursor-wait disabled:opacity-60"
                    >
                      <TrashIcon className="w-4 h-4" />
                      {isRemoving ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      {selectedBook && (
        <div className="downloaded-reader-modal fixed inset-0 z-50 bg-black/90 flex flex-col">
          <header className="w-full bg-white flex items-center justify-between gap-3 px-3 sm:px-4 py-2 shadow-md flex-none">
            <h3 className="text-sm font-semibold text-primary-700 truncate min-w-0">
              {selectedBook.title}
            </h3>
            <div className="flex items-center gap-1 flex-none">
              <button
                type="button"
                onClick={(event) =>
                  toggleFavorite(selectedBook.id, event)
                }
                className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                aria-label={
                  favoriteIdSet.has(String(selectedBook.id))
                    ? 'Remove from favorites'
                    : 'Add to favorites'
                }
              >
                {favoriteIdSet.has(String(selectedBook.id)) ? (
                  <HeartSolid className="w-5 h-5 text-red-500" />
                ) : (
                  <HeartOutline className="w-5 h-5 text-gray-500" />
                )}
              </button>
              <button
                type="button"
                onClick={closeReader}
                className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close PDF reader"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>
          </header>

          <div
            className="relative flex-1 min-h-0 w-full bg-white overflow-auto overscroll-contain"
            onContextMenu={(event) => event.preventDefault()}
          >
            {pdfLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20">
                <div className="w-3/4 max-w-md">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 transition-[width] duration-300 ease-out"
                      style={{ width: `${pdfProgress}%` }}
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-600 text-center tabular-nums">
                    {pdfProgress < 96
                      ? `Opening ${Math.round(pdfProgress)}%`
                      : 'Rendering PDF...'}
                  </p>
                </div>
              </div>
            )}

            {pdfError && !pdfLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-10 px-5">
                <div className="text-center max-w-sm">
                  <BookOpenIcon className="w-12 h-12 text-red-300 mx-auto mb-3" />
                  <p className="font-medium text-red-600">
                    Failed to open PDF
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {pdfError}
                  </p>
                  <button
                    type="button"
                    onClick={() => openBook(selectedBook)}
                    className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}

            {selectedBook.pdfUrl && (
              <Worker workerUrl={PDF_WORKER_URL}>
                <Viewer
                  key={selectedBook.pdfUrl}
                  fileUrl={selectedBook.pdfUrl}
                  plugins={[defaultLayoutPluginInstance]}
                  defaultScale={1}
                  onDocumentLoad={handleDocumentLoad}
                  onDocumentError={handleDocumentError}
                />
              </Worker>
            )}
          </div>
        </div>
      )}
    </>
  );
}